import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState, type ReactNode } from "react"
import { useParams } from "react-router-dom"
import { toast } from "sonner"

import { AdminPageHeader } from "@/components/AdminPageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { api } from "@/lib/client"
import { formatMoney, formatOptionPairs } from "@/lib/format"
import { queryKeys } from "@/lib/queryKeys"
import { cn } from "@/lib/utils"
import { PRODUCT_KIND_LABELS, type AdminBom, type ProductKind, type ProductVariant } from "@/lib/types"
import { ProductImagesFields } from "@/pages/catalog/ProductImagesCard"

const BASE_KEY = "base"

type DraftLine = { componentProductId: number; quantity: number; unit: string }
type OptionPair = { attribute: string; name: string }

function linesFromBom(bom: AdminBom | null | undefined): DraftLine[] {
  return (bom?.lines ?? []).map((l) => ({
    componentProductId: l.componentProductId,
    quantity: l.quantity,
    unit: l.unit,
  }))
}

export function ProductDetailPage() {
  const { productId = "" } = useParams()
  const queryClient = useQueryClient()
  const detail = useQuery({
    queryFn: () => api.product(productId),
    queryKey: queryKeys.product(productId),
  })
  const ingredients = useQuery({
    queryFn: () => api.products({ kind: "ingredient" }),
    queryKey: queryKeys.products({ kind: "ingredient" }),
  })
  const packaging = useQuery({
    queryFn: () => api.products({ kind: "packaging" }),
    queryKey: queryKeys.products({ kind: "packaging" }),
  })
  const product = detail.data?.product
  const variants = detail.data?.variants ?? []
  const [name, setName] = useState("")
  const [sku, setSku] = useState("")
  const [kind, setKind] = useState<ProductKind>("finished_good")
  const [price, setPrice] = useState("0")
  const [active, setActive] = useState("true")
  const [soldOut, setSoldOut] = useState("false")
  const [imageUrlLight, setImageUrlLight] = useState("")
  const [imageUrlDark, setImageUrlDark] = useState("")
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [bomLines, setBomLines] = useState<DraftLine[]>([])
  const [explodeQty, setExplodeQty] = useState("1")
  const [preview, setPreview] = useState<Array<{ name: string; quantity: number; unit: string; sku: string }>>([])
  const [variantName, setVariantName] = useState("")
  const [variantSku, setVariantSku] = useState("")
  const [variantPrice, setVariantPrice] = useState("0")
  const [variantSoldOut, setVariantSoldOut] = useState("false")
  const [variantDefault, setVariantDefault] = useState("false")
  const [addOpen, setAddOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<number | null>(null)

  const defaultKey = useMemo(() => {
    const fallback = variants.find((v) => v.isDefault) ?? variants[0]
    return fallback ? String(fallback.id) : BASE_KEY
  }, [variants])
  const activeKey =
    selectedKey === BASE_KEY || (selectedKey && variants.some((v) => String(v.id) === selectedKey))
      ? selectedKey
      : defaultKey
  const selectedVariant = variants.find((v) => String(v.id) === activeKey)
  const selectedBom = activeKey === BASE_KEY ? detail.data?.productBom : selectedVariant?.bom

  useEffect(() => {
    if (!product) return
    setName(product.name)
    setSku(product.sku)
    setKind(product.kind)
    setPrice(String(product.price))
    setActive(product.active ? "true" : "false")
    setSoldOut(product.soldOut ? "true" : "false")
    setImageUrlLight(product.imageUrlLight ?? "")
    setImageUrlDark(product.imageUrlDark ?? "")
  }, [product])

  useEffect(() => {
    setBomLines(linesFromBom(selectedBom))
    setPreview([])
  }, [selectedBom, activeKey])

  useEffect(() => {
    if (!selectedVariant) return
    setVariantName(selectedVariant.name)
    setVariantSku(selectedVariant.sku)
    setVariantPrice(String(selectedVariant.price))
    setVariantSoldOut(selectedVariant.soldOut ? "true" : "false")
    setVariantDefault(selectedVariant.isDefault ? "true" : "false")
  }, [selectedVariant])

  const save = useMutation({
    mutationFn: () =>
      api.updateProduct(Number(productId), {
        active: active === "true",
        kind,
        name,
        price: Number(price),
        sku,
        soldOut: soldOut === "true",
        imageUrlLight: imageUrlLight.trim() || null,
        imageUrlDark: imageUrlDark.trim() || null,
      }),
    onError: (err) => toast.error(err.message),
    onSuccess: async () => {
      toast.success("Product saved")
      await queryClient.invalidateQueries({ queryKey: queryKeys.product(productId) })
    },
  })

  const saveBom = useMutation({
    mutationFn: () =>
      api.putBom({
        lines: bomLines.filter((l) => l.componentProductId && l.quantity > 0),
        productId: Number(productId),
        variantId: activeKey === BASE_KEY ? null : Number(activeKey),
      }),
    onError: (err) => toast.error(err.message),
    onSuccess: async () => {
      toast.success(selectedVariant ? `BOM saved for ${selectedVariant.name}` : "Base BOM saved")
      await queryClient.invalidateQueries({ queryKey: queryKeys.product(productId) })
    },
  })

  const saveVariant = useMutation({
    mutationFn: () => {
      if (!selectedVariant) throw new Error("No variant selected")
      return api.updateVariant(Number(productId), selectedVariant.id, {
        isDefault: variantDefault === "true",
        name: variantName,
        price: Number(variantPrice),
        sku: variantSku,
        soldOut: variantSoldOut === "true",
      })
    },
    onError: (err) => toast.error(err.message),
    onSuccess: async () => {
      toast.success("Variant saved")
      await queryClient.invalidateQueries({ queryKey: queryKeys.product(productId) })
    },
  })

  const explode = useMutation({
    mutationFn: () =>
      api.explodeBom({
        productId: Number(productId),
        quantity: Number(explodeQty) || 1,
        variantId: activeKey === BASE_KEY ? null : Number(activeKey),
      }),
    onError: (err) => toast.error(err.message),
    onSuccess: (data) => setPreview(data.components),
  })

  const removeVariant = useMutation({
    mutationFn: (id: number) => api.deleteVariant(Number(productId), id),
    onError: (err) => toast.error(err.message),
    onSuccess: async (_, id) => {
      toast.success("Variant removed")
      setDeleteId(null)
      if (activeKey === String(id)) setSelectedKey(defaultKey === String(id) ? BASE_KEY : defaultKey)
      await queryClient.invalidateQueries({ queryKey: queryKeys.product(productId) })
      await queryClient.invalidateQueries({ queryKey: ["products"] })
    },
  })

  const components = [...(ingredients.data?.products ?? []), ...(packaging.data?.products ?? [])]
  const copySources = [
    { key: BASE_KEY, label: "Base recipe" },
    ...variants.map((v) => ({ key: String(v.id), label: v.name })),
  ]

  if (detail.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>
  if (!product) return <p className="text-sm text-destructive">Product not found.</p>

  return (
    <div className="space-y-6">
      <AdminPageHeader description={product.sku} title={product.name}>
        <Button disabled={save.isPending} onClick={() => save.mutate()} size="sm" type="button">
          Save product
        </Button>
      </AdminPageHeader>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Details</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <ProductImagesFields
            imageUrlDark={imageUrlDark}
            imageUrlLight={imageUrlLight}
            onDarkChange={setImageUrlDark}
            onLightChange={setImageUrlLight}
            productId={product.id}
          />
          <Field label="Name">
            <Input onChange={(e) => setName(e.target.value)} value={name} />
          </Field>
          <Field label="SKU">
            <Input onChange={(e) => setSku(e.target.value)} value={sku} />
          </Field>
          <Field label="Kind">
            <Select onValueChange={(v) => setKind(v as ProductKind)} value={kind}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PRODUCT_KIND_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Price">
            <Input onChange={(e) => setPrice(e.target.value)} type="number" value={price} />
          </Field>
          <Field label="Active">
            <Select onValueChange={setActive} value={active}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Yes</SelectItem>
                <SelectItem value="false">No</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Sold out">
            <Select onValueChange={setSoldOut} value={soldOut}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Yes</SelectItem>
                <SelectItem value="false">No</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">Variants</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Each option combination can have its own recipe. Prep and stock use the variant BOM, not
              the product average.
            </p>
          </div>
          <Button onClick={() => setAddOpen(true)} size="sm" type="button">
            Add variant
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Variant</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Options</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>BOM</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow
                className={cn("cursor-pointer", activeKey === BASE_KEY && "bg-muted/70")}
                onClick={() => setSelectedKey(BASE_KEY)}
              >
                <TableCell className="font-medium">Base recipe</TableCell>
                <TableCell className="text-muted-foreground">{product.sku}</TableCell>
                <TableCell className="text-muted-foreground">Fallback if a variant has no BOM</TableCell>
                <TableCell>{formatMoney(product.price, product.currency)}</TableCell>
                <TableCell>
                  <BomBadge lines={detail.data?.productBom?.lines.length ?? 0} />
                </TableCell>
                <TableCell />
              </TableRow>
              {variants.map((variant) => (
                <TableRow
                  className={cn("cursor-pointer", activeKey === String(variant.id) && "bg-muted/70")}
                  key={variant.id}
                  onClick={() => setSelectedKey(String(variant.id))}
                >
                  <TableCell className="font-medium">
                    <span className="flex flex-wrap items-center gap-2">
                      {variant.name}
                      {variant.isDefault ? <Badge variant="secondary">Default</Badge> : null}
                      {variant.soldOut ? <Badge variant="outline">Sold out</Badge> : null}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{variant.sku}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatOptionPairs(variant.optionPairs) || "—"}
                  </TableCell>
                  <TableCell>{formatMoney(variant.price, product.currency)}</TableCell>
                  <TableCell>
                    <BomBadge lines={variant.bom?.lines.length ?? 0} />
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation()
                        setDeleteId(variant.id)
                      }}
                      size="sm"
                      type="button"
                      variant="ghost"
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {selectedVariant ? (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Selected variant</CardTitle>
            <Button disabled={saveVariant.isPending} onClick={() => saveVariant.mutate()} size="sm" type="button">
              Save variant
            </Button>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <Field label="Name">
              <Input onChange={(e) => setVariantName(e.target.value)} value={variantName} />
            </Field>
            <Field label="SKU">
              <Input onChange={(e) => setVariantSku(e.target.value)} value={variantSku} />
            </Field>
            <Field label="Price">
              <Input onChange={(e) => setVariantPrice(e.target.value)} type="number" value={variantPrice} />
            </Field>
            <Field label="Sold out">
              <Select onValueChange={setVariantSoldOut} value={variantSoldOut}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Default">
              <Select onValueChange={setVariantDefault} value={variantDefault}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">
              {selectedVariant ? `BOM · ${selectedVariant.name}` : "BOM · Base recipe"}
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              {selectedVariant
                ? "This recipe is exploded when this variant is ordered."
                : "Used when a variant has no recipe of its own."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" type="button" variant="outline">
                  Copy BOM from
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {copySources.map((source) => (
                  <DropdownMenuItem
                    key={source.key}
                    onClick={() => {
                      const bom =
                        source.key === BASE_KEY
                          ? detail.data?.productBom
                          : variants.find((v) => String(v.id) === source.key)?.bom
                      setBomLines(linesFromBom(bom))
                    }}
                  >
                    {source.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button disabled={saveBom.isPending} onClick={() => saveBom.mutate()} size="sm" type="button">
              Save BOM
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <BomLinesTable
            components={components}
            lines={bomLines}
            onChange={setBomLines}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-base">Explode preview</CardTitle>
          <div className="flex items-center gap-2">
            <Input className="w-20" onChange={(e) => setExplodeQty(e.target.value)} value={explodeQty} />
            <Button disabled={explode.isPending} onClick={() => explode.mutate()} size="sm" type="button">
              Explode
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {preview.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Run explode to see component quantities for the selected variant.
            </p>
          ) : (
            <ul className="space-y-1 text-sm">
              {preview.map((c) => (
                <li key={c.sku}>
                  {c.quantity} {c.unit} {c.name}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <AddVariantDialog
        onCreated={async (id) => {
          setSelectedKey(String(id))
          await queryClient.invalidateQueries({ queryKey: queryKeys.product(productId) })
          await queryClient.invalidateQueries({ queryKey: ["products"] })
        }}
        onOpenChange={setAddOpen}
        open={addOpen}
        productId={Number(productId)}
        productName={product.name}
        productPrice={product.price}
        productSku={product.sku}
        variants={variants}
      />

      <Dialog onOpenChange={(open) => !open && setDeleteId(null)} open={deleteId != null}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove variant?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This deletes the variant and its BOM. Orders that already used it will keep their snapshot.
            Variants still referenced by open order lines cannot be removed.
          </p>
          <DialogFooter>
            <Button onClick={() => setDeleteId(null)} type="button" variant="outline">
              Cancel
            </Button>
            <Button
              disabled={deleteId == null || removeVariant.isPending}
              onClick={() => deleteId != null && removeVariant.mutate(deleteId)}
              type="button"
              variant="destructive"
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function BomBadge({ lines }: { lines: number }) {
  if (lines === 0) {
    return <span className="text-muted-foreground">None</span>
  }
  return (
    <Badge variant="secondary">
      {lines} line{lines === 1 ? "" : "s"}
    </Badge>
  )
}

function BomLinesTable({
  components,
  lines,
  onChange,
}: {
  components: Array<{ id: number; name: string }>
  lines: DraftLine[]
  onChange: (lines: DraftLine[]) => void
}) {
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Component</TableHead>
            <TableHead>Qty</TableHead>
            <TableHead>Unit</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((line, index) => (
            <TableRow key={`${line.componentProductId}-${index}`}>
              <TableCell>
                <Select
                  onValueChange={(v) => {
                    const next = [...lines]
                    next[index] = { ...line, componentProductId: Number(v) }
                    onChange(next)
                  }}
                  value={String(line.componentProductId)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Component" />
                  </SelectTrigger>
                  <SelectContent>
                    {components.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                <Input
                  onChange={(e) => {
                    const next = [...lines]
                    next[index] = { ...line, quantity: Number(e.target.value) }
                    onChange(next)
                  }}
                  type="number"
                  value={line.quantity}
                />
              </TableCell>
              <TableCell>
                <Input
                  onChange={(e) => {
                    const next = [...lines]
                    next[index] = { ...line, unit: e.target.value }
                    onChange(next)
                  }}
                  value={line.unit}
                />
              </TableCell>
              <TableCell>
                <Button
                  onClick={() => onChange(lines.filter((_, i) => i !== index))}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  Remove
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Button
        onClick={() =>
          onChange([...lines, { componentProductId: components[0]?.id ?? 0, quantity: 1, unit: "each" }])
        }
        size="sm"
        type="button"
        variant="outline"
      >
        Add line
      </Button>
    </>
  )
}

function AddVariantDialog({
  onCreated,
  onOpenChange,
  open,
  productId,
  productName,
  productPrice,
  productSku,
  variants,
}: {
  onCreated: (id: number) => Promise<void>
  onOpenChange: (open: boolean) => void
  open: boolean
  productId: number
  productName: string
  productPrice: number
  productSku: string
  variants: ProductVariant[]
}) {
  const defaultVariant = variants.find((v) => v.isDefault) ?? variants[0]
  const [name, setName] = useState("")
  const [sku, setSku] = useState("")
  const [price, setPrice] = useState(String(productPrice))
  const [copyFrom, setCopyFrom] = useState(defaultVariant ? String(defaultVariant.id) : BASE_KEY)
  const [pairs, setPairs] = useState<OptionPair[]>([{ attribute: "", name: "" }])
  const [skuTouched, setSkuTouched] = useState(false)

  useEffect(() => {
    if (!open) return
    setName("")
    setSku(`${productSku}-`)
    setPrice(String(productPrice))
    setCopyFrom(defaultVariant ? String(defaultVariant.id) : BASE_KEY)
    setPairs([{ attribute: "", name: "" }])
    setSkuTouched(false)
  }, [defaultVariant, open, productPrice, productSku])

  const create = useMutation({
    mutationFn: () =>
      api.createVariant(productId, {
        copyBomFromVariantId: copyFrom === BASE_KEY ? null : Number(copyFrom),
        name: name.trim() || `${productName} (variant)`,
        optionPairs: pairs.filter((p) => p.attribute.trim() && p.name.trim()),
        price: Number(price),
        sku: sku.trim(),
      }),
    onError: (err) => toast.error(err.message),
    onSuccess: async (data) => {
      toast.success("Variant created")
      onOpenChange(false)
      await onCreated(data.variant.id)
    },
  })

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add variant</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Name">
            <Input
              onChange={(e) => {
                const next = e.target.value
                setName(next)
                if (!skuTouched) {
                  const suffix = next
                    .trim()
                    .toLowerCase()
                    .replaceAll(/[^a-z0-9]+/g, "-")
                    .replaceAll(/^-|-$/g, "")
                  setSku(suffix ? `${productSku}-${suffix}` : `${productSku}-`)
                }
              }}
              placeholder={`${productName} (Brown rice)`}
              value={name}
            />
          </Field>
          <Field label="SKU">
            <Input
              onChange={(e) => {
                setSkuTouched(true)
                setSku(e.target.value)
              }}
              value={sku}
            />
          </Field>
          <Field label="Price">
            <Input onChange={(e) => setPrice(e.target.value)} type="number" value={price} />
          </Field>
          <Field label="Copy BOM from">
            <Select onValueChange={setCopyFrom} value={copyFrom}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={BASE_KEY}>Base recipe</SelectItem>
                {variants.map((variant) => (
                  <SelectItem key={variant.id} value={String(variant.id)}>
                    {variant.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="space-y-2">
            <Label>Options</Label>
            {pairs.map((pair, index) => (
              <div className="grid grid-cols-2 gap-2" key={index}>
                <Input
                  onChange={(e) => {
                    const next = [...pairs]
                    next[index] = { ...pair, attribute: e.target.value }
                    setPairs(next)
                  }}
                  placeholder="Grain"
                  value={pair.attribute}
                />
                <Input
                  onChange={(e) => {
                    const next = [...pairs]
                    next[index] = { ...pair, name: e.target.value }
                    setPairs(next)
                  }}
                  placeholder="Brown rice"
                  value={pair.name}
                />
              </div>
            ))}
            <Button
              onClick={() => setPairs([...pairs, { attribute: "", name: "" }])}
              size="sm"
              type="button"
              variant="outline"
            >
              Add option
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button disabled={!sku.trim() || create.isPending} onClick={() => create.mutate()} type="button">
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Field({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  )
}
